import React, { useState, useRef, useEffect } from 'react';
import { devError } from '../lib/devLog';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Move, ZoomIn, ZoomOut, RotateCw, Camera, Maximize2, Mail, CheckCircle2, Frame as FrameIcon } from 'lucide-react';
import PhoneField, { isPhoneValid } from './PhoneField';
import { FRAME_PRESETS, MAT_COLOR } from './visualizer/framePresets';
import SavePreviewModal from './visualizer/SavePreviewModal';
import QuickGuide from './visualizer/QuickGuide';
import BottomControls from './visualizer/BottomControls';
import composePreview from './visualizer/composePreview';

const ViewOnWall = ({ artwork, isOpen, onClose }) => {
  const [roomImage, setRoomImage] = useState(null);
  const [artworkPosition, setArtworkPosition] = useState({ x: 50, y: 50 });
  const [artworkScale, setArtworkScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [showGuide, setShowGuide] = useState(true);
  // User-calibrated photo width in cm — how much wall the photo actually captures.
  // Default 250cm is typical for a full-wall snap; users can adjust to match reality.
  const [wallWidthCm, setWallWidthCm] = useState(250);
  // Pinch-to-zoom state (two-finger gestures)
  const [pinchStart, setPinchStart] = useState(null); // { distance, scale }
  // Frame preset (none | black | white | natural | gold)
  const [framePreset, setFramePreset] = useState('none');
  const frame = FRAME_PRESETS[framePreset] || FRAME_PRESETS.none;
  // Save & email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [emailStatus, setEmailStatus] = useState({ sending: false, sent: false, error: '' });
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Natural pixel dimensions of the artwork image — used to derive the true
  // orientation (portrait vs landscape) so the frame box matches the artwork.
  const [imgNatural, setImgNatural] = useState(null);

  // Preload the artwork image so we know its real aspect ratio before
  // deciding whether to render the frame as portrait or landscape.
  useEffect(() => {
    if (!artwork?.imageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => setImgNatural(null);
    img.src = artwork.imageUrl?.startsWith('http')
      ? artwork.imageUrl
      : `${process.env.REACT_APP_BACKEND_URL}${artwork.imageUrl}`;
  }, [artwork?.imageUrl]);

  // Parse dimensions to get width for proper scaling
  const getArtworkDimensions = () => {
    if (!artwork.dimensions) return { width: 50, height: 70 };
    const match = artwork.dimensions.match(/(\d+\.?\d*)\s*x\s*(\d+\.?\d*)/i);
    if (match) {
      let w = parseFloat(match[1]);
      let h = parseFloat(match[2]);
      if (artwork.dimensions.toLowerCase().includes('mm')) { w /= 10; h /= 10; }
      // Catalog convention isn't consistent (some galleries write H x W,
      // others W x H). Use the actual image orientation to reassign the
      // long/short side correctly: long parsed dim → long image side.
      if (imgNatural) {
        const longParsed  = Math.max(w, h);
        const shortParsed = Math.min(w, h);
        const imgPortrait = imgNatural.h >= imgNatural.w;
        return imgPortrait
          ? { width: shortParsed, height: longParsed }   // portrait: tall side is height
          : { width: longParsed,  height: shortParsed }; // landscape: wide side is width
      }
      return { width: w, height: h };
    }
    return { width: 50, height: 70 };
  };

  const dims = getArtworkDimensions();
  const aspectRatio = imgNatural
    ? imgNatural.w / imgNatural.h            // prefer the real pixel aspect
    : dims.width / dims.height;

  // Camera access: try live in-browser camera first (great on desktop),
  // fall back to the native camera app via <input capture> when blocked
  // (iOS Safari, locked-down WebViews, denied permissions, etc.)
  const startCamera = async () => {
    // Detect mobile: native camera capture is the canonical UX on phones,
    // skip getUserMedia which often fails or feels alien on iOS.
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(
      typeof navigator !== 'undefined' ? navigator.userAgent : ''
    );
    if (isMobile) {
      cameraInputRef.current?.click();
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }, // Prefer back camera but allow front
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 }
        }
      });

      setStream(mediaStream);
      setIsCameraActive(true);
      setRoomImage(null); // Clear any uploaded image

      // Wait for next tick to ensure videoRef is available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(e => devError('Video play error:', e));
        }
      }, 100);
    } catch (error) {
      devError('Camera access error:', error);
      // Don't show a scary alert — just fall back to the native camera intent
      cameraInputRef.current?.click();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !videoRef.current.videoWidth) {
      alert('Camera is still loading. Please wait a moment and try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.95);
    setRoomImage(imageData);
    stopCamera();
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setRoomImage(event.target.result);
        stopCamera();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseDown = (e) => {
    const artworkEl = e.target.closest('.artwork-preview');
    if (!artworkEl) return;

    e.preventDefault();

    // Two-finger pinch: start pinch gesture instead of drag
    if (e.touches && e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const distance = Math.hypot(dx, dy);
      setPinchStart({ distance, scale: artworkScale });
      setIsDragging(false);
      return;
    }

    setIsDragging(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;

    setDragStart({
      x: clientX - (artworkPosition.x * rect.width / 100),
      y: clientY - (artworkPosition.y * rect.height / 100)
    });
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current) return;

    // Two-finger pinch in progress → resize artwork
    if (pinchStart && e.touches && e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const distance = Math.hypot(dx, dy);
      const ratio = distance / pinchStart.distance;
      const nextScale = Math.max(0.3, Math.min(3, pinchStart.scale * ratio));
      setArtworkScale(nextScale);
      return;
    }

    if (!isDragging) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;

    const x = ((clientX - dragStart.x) / rect.width) * 100;
    const y = ((clientY - dragStart.y) / rect.height) * 100;

    setArtworkPosition({
      x: Math.max(10, Math.min(90, x)),
      y: Math.max(10, Math.min(90, y))
    });
  };

  const handleMouseUp = (e) => {
    // If the user lifts one finger mid-pinch, end pinch gesture
    if (pinchStart) {
      setPinchStart(null);
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging || pinchStart) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      window.addEventListener('touchcancel', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
        window.removeEventListener('touchcancel', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, pinchStart, dragStart]);

  // Show the quick guide for ~6 seconds after a room photo is set so users
  // discover pinch-to-resize and wall-width calibration. Then auto-hide.
  useEffect(() => {
    if (roomImage) {
      setShowGuide(true);
      const t = setTimeout(() => setShowGuide(false), 6000);
      return () => clearTimeout(t);
    }
    setShowGuide(true);
  }, [roomImage]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adjustScale = (delta) => {
    setArtworkScale(prev => Math.max(0.3, Math.min(3, prev + delta)));
  };

  const resetPosition = () => {
    setArtworkPosition({ x: 50, y: 50 });
    setArtworkScale(1);
  };

  // Compose the room photo + positioned artwork into a single PNG blob.
  // Mirrors what the user sees on screen — see ./visualizer/composePreview.js.
  const handleComposePreview = () => composePreview({
    roomImage,
    artworkSrc: artwork.imageUrl?.startsWith('http')
      ? artwork.imageUrl
      : `${process.env.REACT_APP_BACKEND_URL}${artwork.imageUrl}`,
    artworkPosition,
    artworkScale,
    wallWidthCm,
    dims,
    frame,
    framePreset,
  });

  const handleSendPreview = async (e) => {
    e?.preventDefault();
    if (!isPhoneValid(emailForm.phone)) {
      setEmailStatus({ sending: false, sent: false, error: 'Please enter a valid phone number with country code.' });
      return;
    }
    setEmailStatus({ sending: true, sent: false, error: '' });
    try {
      const blob = await handleComposePreview();
      const fd = new FormData();
      fd.append('name', emailForm.name.trim());
      fd.append('email', emailForm.email.trim());
      fd.append('phone', emailForm.phone || '');
      fd.append('artwork_id', artwork.id || '');
      fd.append('artwork_title', artwork.title || '');
      fd.append('artwork_image_url', artwork.imageUrl || '');
      fd.append('artwork_artist', artwork.artistName || '');
      fd.append('message', emailForm.message.trim());
      fd.append('file', blob, 'wall-preview.png');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visualizer/email-preview`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Server returned ${res.status}`);
      }
      setEmailStatus({ sending: false, sent: true, error: '' });
    } catch (err) {
      setEmailStatus({ sending: false, sent: false, error: err.message || 'Could not send preview' });
    }
  };

  // Artwork sizing: scale relative to the room photo using the user-calibrated
  // wall width. If a photo captures 250 cm of wall, a 50 cm artwork takes up
  // 50/250 = 20% of the photo width. Users calibrate via the slider to match
  // what their actual photo shows.
  const artworkWidthPercent = Math.min(
    95,
    (dims.width / wallWidthCm) * 100 * artworkScale
  );
  // Height derived from artwork aspect ratio — CSS keeps it locked
  const artworkSize = {
    widthPercent: artworkWidthPercent,
    aspectRatio: aspectRatio,
  };

  // ── Framed sizing in real-world cm ──────────────────────────────────────
  // Custom-framed gallery piece: thin wood moulding + cream mat + print.
  // The total framed footprint = artwork sheet size + 2×(mat + moulding) on
  // every side. This grows outward so the artwork keeps its true dimensions.
  const totalFramedWcm = dims.width  + 2 * (frame.frameCm + frame.matCm);
  const totalFramedHcm = dims.height + 2 * (frame.frameCm + frame.matCm);
  const framedWidthPercent = Math.min(
    95,
    (totalFramedWcm / wallWidthCm) * 100 * artworkScale
  );
  const framedAspect = totalFramedWcm / totalFramedHcm;
  // % padding values — these will be applied to an INNER div whose parent
  // is the framed wrap itself (so the % resolves against the wrap's width,
  // not against the canvas — fixes the previous proportion bug).
  const framePadPct = totalFramedWcm > 0 ? (frame.frameCm / totalFramedWcm) * 100 : 0;
  const innerAfterFrameWcm = totalFramedWcm - 2 * frame.frameCm;
  const matPadPct = innerAfterFrameWcm > 0 ? (frame.matCm / innerAfterFrameWcm) * 100 : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="w-full h-full flex items-center justify-center"
      >
        <div className="relative w-full h-full flex flex-col">
          {/* Minimal Header - Top overlay - hide with Quick Guide */}
          {showGuide && roomImage && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-0 left-0 right-0 z-20 p-4 sm:p-6 flex items-start justify-between pointer-events-none"
            >
              <div className="bg-black/60 backdrop-blur-xl px-4 sm:px-6 py-3 rounded-2xl pointer-events-auto border border-white/10">
                <h3 className="text-white font-medium text-base sm:text-lg">{artwork.title}</h3>
                <p className="text-neutral-300 text-xs sm:text-sm mt-1">{artwork.dimensions} • {artwork.artistName}</p>
              </div>
            </motion.div>
          )}

          {/* Close button - always visible */}
          <div className="absolute top-0 right-0 z-20 p-4 sm:p-6 pointer-events-none">
            <button
              onClick={onClose}
              className="bg-black/60 backdrop-blur-xl p-3 rounded-2xl hover:bg-black/80 transition-colors pointer-events-auto border border-white/10"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>
          </div>

          {/* Full-screen Canvas */}
          <div
            ref={canvasRef}
            className="flex-1 relative"
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            style={{ cursor: isDragging ? 'grabbing' : 'default' }}
          >
            {!roomImage && !isCameraActive ? (
              /* Initial state - Camera/Upload choice */
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 sm:p-12">
                <div className="max-w-2xl w-full space-y-8 text-center">
                  <div className="space-y-4">
                    <Maximize2 className="w-12 h-12 text-white mx-auto opacity-50" />
                    <h2 className="text-white text-3xl sm:text-4xl font-light">
                      Visualize on Your Wall
                    </h2>
                    <p className="text-neutral-300 text-base max-w-xl mx-auto">
                      See exactly how this artwork will look in your space with accurate, real-world scaling
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mt-12 max-w-3xl mx-auto">
                    <button
                      onClick={startCamera}
                      data-testid="vow-use-camera-btn"
                      className="group relative overflow-hidden bg-white/10 backdrop-blur-xl border-2 border-white/20 hover:border-white/40 rounded-2xl p-6 transition-all hover:scale-105 active:scale-95"
                    >
                      <Camera className="w-10 h-10 text-white mx-auto mb-3" />
                      <h3 className="text-white text-lg font-medium mb-2">Use Camera</h3>
                      <p className="text-neutral-300 text-sm">
                        Take a live photo of your wall
                      </p>
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/10 group-hover:to-purple-500/10 transition-all"></div>
                    </button>

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="vow-upload-photo-btn"
                      className="group relative overflow-hidden bg-white/10 backdrop-blur-xl border-2 border-white/20 hover:border-white/40 rounded-2xl p-6 transition-all hover:scale-105 active:scale-95"
                    >
                      <Upload className="w-10 h-10 text-white mx-auto mb-3" />
                      <h3 className="text-white text-lg font-medium mb-2">Upload Photo</h3>
                      <p className="text-neutral-300 text-sm">
                        Choose an existing photo
                      </p>
                      <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-teal-500/0 group-hover:from-green-500/10 group-hover:to-teal-500/10 transition-all"></div>
                    </button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="vow-upload-input"
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="vow-camera-input"
                  />
                </div>
              </div>
            ) : isCameraActive ? (
              /* Live camera view - FULL SCREEN */
              <div className="absolute inset-0 bg-black">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ 
                    minWidth: '100%', 
                    minHeight: '100%',
                    width: '100vw',
                    height: '100vh'
                  }}
                />
                
                {/* Camera controls overlay - LARGER */}
                <div className="absolute bottom-12 left-0 right-0 flex flex-col items-center gap-6 z-20">
                  <button
                    onClick={capturePhoto}
                    className="w-16 h-16 bg-white rounded-full hover:scale-110 transition-transform shadow-2xl flex items-center justify-center"
                  >
                    <div className="w-12 h-12 bg-white border-4 border-black rounded-full"></div>
                  </button>
                  <button
                    onClick={stopCamera}
                    className="px-6 py-3 bg-white/20 backdrop-blur-xl text-white rounded-full hover:bg-white/30 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>

                {/* Camera instruction overlay */}
                <div className="absolute top-24 left-0 right-0 flex justify-center z-10">
                  <div className="bg-black/60 backdrop-blur-xl px-6 py-3 rounded-full">
                    <p className="text-white text-sm sm:text-base">Point camera at your wall and tap to capture</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Photo captured - show artwork overlay */
              <>
                <img
                  src={roomImage}
                  alt="Your room"
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  draggable={false}
                />

                {/* Custom-framed artwork — thin wood moulding + generous cream mat
                    + print sitting inside (object-contain to preserve the print's
                    own paper margin). Matches IMG_9224 reference. */}
                <div
                  className="artwork-preview absolute touch-none"
                  style={{
                    left: `${artworkPosition.x}%`,
                    top: `${artworkPosition.y}%`,
                    transform: 'translate(-50%, -50%)',
                    width: `${framedWidthPercent}%`,
                    aspectRatio: `${framedAspect}`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    transition: isDragging ? 'none' : 'all 0.3s ease',
                    pointerEvents: 'auto',
                    zIndex: 10,
                  }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleMouseDown}
                >
                  {framePreset === 'none' ? (
                    /* No frame — just the print itself */
                    <img
                      src={artwork.imageUrl?.startsWith('http') ? artwork.imageUrl : `${process.env.REACT_APP_BACKEND_URL}${artwork.imageUrl}`}
                      alt={artwork.title}
                      className="w-full h-full object-contain block"
                      draggable={false}
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    />
                  ) : (
                    /* Frame → Mat → Print (each % resolves against its parent,
                       fixing the prior canvas-relative padding bug). */
                    <div
                      style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        padding: `${framePadPct}%`,
                        background: frame.frameBg,
                        boxShadow: frame.shadow,
                        boxSizing: 'border-box',
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          padding: `${matPadPct}%`,
                          background: MAT_COLOR,
                          boxSizing: 'border-box',
                          // Subtle inner bevel where mat meets frame opening
                          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
                        }}
                      >
                        <img
                          src={artwork.imageUrl?.startsWith('http') ? artwork.imageUrl : `${process.env.REACT_APP_BACKEND_URL}${artwork.imageUrl}`}
                          alt={artwork.title}
                          className="w-full h-full object-contain block"
                          draggable={false}
                          style={{
                            pointerEvents: 'none',
                            userSelect: 'none',
                            // Soft drop-shadow under the print on the mat
                            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.12))',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Controls - Bottom overlay (only show when image is set) */}
          {roomImage && (
            <>
              <BottomControls
                framePreset={framePreset}
                setFramePreset={setFramePreset}
                wallWidthCm={wallWidthCm}
                setWallWidthCm={setWallWidthCm}
                artworkScale={artworkScale}
                adjustScale={adjustScale}
                resetPosition={resetPosition}
                startCamera={startCamera}
                fileInputRef={fileInputRef}
                handleFileUpload={handleFileUpload}
                onOpenEmailModal={() => {
                  setEmailStatus({ sending: false, sent: false, error: '' });
                  setShowEmailModal(true);
                }}
              />

              {/* Instructions - Right side - Auto-hide after 5 seconds */}
              <QuickGuide visible={showGuide} onDismiss={() => setShowGuide(false)} />
            </>
          )}
        </div>
      </motion.div>

      <SavePreviewModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        artwork={artwork}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        emailStatus={emailStatus}
        onSubmit={handleSendPreview}
      />
    </div>
  );
};

export default ViewOnWall;
